import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError, TOKEN_KEY, USER_KEY } from "@/lib/api";
import { LogIn, UserPlus } from "lucide-react";

const LOGO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_motoserra-depot/artifacts/18uda418_logo%20Padr%C3%A3o.png";
const AERIAL_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_motoserra-depot/artifacts/25zvi9bh_DCIM_100MEDIA_DJI0679.JPG.webp";

export default function Login() {
  const { user, login } = useAuth();
  const [mode, setMode] = useState("login"); // login | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (user) {
      const dest = user.role === "compras" ? "/compras" : "/";
      nav(dest, { replace: true });
    }
  }, [user, nav]);
  if (user) return <Navigate to={user.role === "compras" ? "/compras" : "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(email.trim(), password);
        toast.success("Bem-vindo!");
        nav(u?.role === "compras" ? "/compras" : "/", { replace: true });
        return;
      } else {
        const { data } = await api.post("/auth/signup", { email: email.trim(), password });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        toast.success("Cadastro realizado! Solicite acesso ao administrador para liberar funções extras.");
        window.location.href = "/";
      }
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Falha");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div
        className="hidden md:flex md:w-1/2 relative text-white p-12 flex-col justify-between overflow-hidden"
        style={{
          backgroundImage: `url('${AERIAL_URL}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Overlay escuro para legibilidade */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A3C1E]/85 via-[#0A3C1E]/70 to-[#0A3C1E]/90" aria-hidden="true" />
        {/* Textura sutil */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 22px, rgba(255,255,255,0.08) 22px 23px)" }}
          aria-hidden="true"
        />

        <div className="relative flex items-center gap-4">
          <img
            src={LOGO_URL}
            alt="COOMFLONA"
            className="w-28 h-28 object-contain shrink-0 drop-shadow-2xl"
          />
          <div>
            <div className="font-display font-black text-3xl uppercase tracking-tight leading-none" style={{ color: "#F3C84B" }}>COOMFLONA</div>
            <div className="font-mono-data text-[10px] uppercase text-white/80 mt-2 tracking-widest">Cooperativa Mista da Flona Tapajós</div>
          </div>
        </div>

        <div className="relative">
          <div className="inline-block mb-3 px-3 py-1 bg-[#FF4F00] text-white font-mono-data text-[10px] uppercase tracking-[0.3em] font-bold">Sistema Interno</div>
          <h1 className="font-display font-black text-5xl uppercase tracking-tighter leading-[0.95] drop-shadow-lg">
            Almoxarifado<br />Florestal
          </h1>
          <p className="text-sm text-white/90 mt-4 max-w-md leading-relaxed">
            Gestão integrada de estoque, requisições, compras e EPIs da cooperativa
            na Floresta Nacional do Tapajós.
          </p>
        </div>

        <div className="relative font-mono-data text-[10px] uppercase tracking-widest text-white/70 flex items-center justify-between">
          <span>CNPJ 07.315.165/0001-00 · Santarém · Pará</span>
          <span className="hidden lg:inline">BR 163 · KM 117</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F0EFEA]">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="COOMFLONA" className="w-16 h-16 object-contain" />
            <div>
              <div className="font-display font-black text-lg uppercase tracking-tight">COOMFLONA</div>
              <div className="font-mono-data text-[9px] uppercase tracking-widest text-[#555]">Almoxarifado</div>
            </div>
          </div>

          <div className="brutal-card p-8">
            {/* Tabs */}
            <div className="flex gap-0 mb-6 border-2 border-[#111]">
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`flex-1 py-2 font-display font-bold uppercase text-xs tracking-widest ${mode === "login" ? "bg-[#FF4F00] text-white" : "bg-white text-[#111]"}`}
                data-testid="tab-login"
              >
                Entrar
              </button>
              <button
                type="button"
                onClick={() => setMode("signup")}
                className={`flex-1 py-2 font-display font-bold uppercase text-xs tracking-widest border-l-2 border-[#111] ${mode === "signup" ? "bg-[#FF4F00] text-white" : "bg-white text-[#111]"}`}
                data-testid="tab-signup"
              >
                Cadastrar
              </button>
            </div>

            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-[#FF4F00]">{mode === "login" ? "Acesso" : "Novo usuário"}</div>
            <h2 className="font-display font-black text-3xl uppercase tracking-tighter mt-1 mb-6">
              {mode === "login" ? "Entrar" : "Cadastrar"}
            </h2>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Email</label>
                <input
                  type="email" required autoFocus autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full border-2 border-[#111] p-2.5 text-sm outline-none focus:border-[#FF4F00] bg-white"
                  data-testid="login-email"
                />
              </div>
              <div>
                <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">
                  Senha {mode === "signup" && <span className="text-[#777] normal-case">(mín. 6 caracteres)</span>}
                </label>
                <input
                  type="password" required minLength={mode === "signup" ? 6 : undefined}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full border-2 border-[#111] p-2.5 text-sm outline-none focus:border-[#FF4F00] bg-white"
                  data-testid="login-password"
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="brutal-btn bg-[#FF4F00] text-white w-full px-4 py-3 font-display font-bold uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2 mt-2"
                data-testid="login-submit"
              >
                {mode === "login" ? <><LogIn className="w-4 h-4" /> {loading ? "Entrando..." : "Entrar"}</>
                  : <><UserPlus className="w-4 h-4" /> {loading ? "Cadastrando..." : "Criar Conta"}</>}
              </button>
            </form>

            {mode === "signup" && (
              <div className="mt-4 p-3 border-2 border-[#FFC107] bg-[#FFF8E1] text-xs font-mono-data text-[#5C4400]">
                Novos cadastros entram com perfil <strong>Setor / Requisição</strong>. Solicite ao administrador para alterar seu perfil.
              </div>
            )}

            <div className="mt-6 pt-4 border-t-2 border-[#111] font-mono-data text-[10px] uppercase tracking-widest text-[#777]">
              v1.0 · Sistema Interno COOMFLONA
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
